import React, { useState, useEffect, useCallback } from 'react';
import {
    Container,
    Box,
    Typography,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    CircularProgress,
    Alert,
    Grid
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import { Cobertura, getCoberturas, createCobertura, updateCobertura, deleteCobertura } from '../services/coberturaService';
import MainLayout from '../layouts/MainLayout';

const GerenciarCoberturas: React.FC = () => {
    const [coberturas, setCoberturas] = useState<Cobertura[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [open, setOpen] = useState<boolean>(false);
    const [isEditing, setIsEditing] = useState<boolean>(false);
    const [currentCobertura, setCurrentCobertura] = useState<Partial<Cobertura>>({ nome: '' });

    const fetchCoberturas = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            const data = await getCoberturas();
            setCoberturas(data);
        } catch (err) {
            setError('Falha ao carregar as coberturas. Tente novamente mais tarde.');
            console.error(err);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchCoberturas();
    }, [fetchCoberturas]);

    const handleOpen = (cobertura: Partial<Cobertura> | null = null) => {
        if (cobertura) {
            setIsEditing(true);
            setCurrentCobertura({ ...cobertura });
        } else {
            setIsEditing(false);
            setCurrentCobertura({ nome: '' });
        }
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
    };

    const handleSave = async () => {
        if (!currentCobertura.nome) {
            toast.warn('O nome da cobertura é obrigatório.');
            return;
        }

        try {
            if (isEditing && currentCobertura.id) {
                await updateCobertura(currentCobertura.id, { nome: currentCobertura.nome });
                toast.success('Cobertura atualizada com sucesso!');
            } else {
                await createCobertura({ nome: currentCobertura.nome });
                toast.success('Cobertura criada com sucesso!');
            }
            fetchCoberturas();
            handleClose();
        } catch (err: any) {
            const errorMessage = err.response?.data?.error || 'Erro ao salvar cobertura.';
            toast.error(errorMessage);
            console.error(err);
        }
    };

    const handleDelete = async (id: number) => {
        if (window.confirm('Tem certeza que deseja excluir esta cobertura?')) {
            try {
                await deleteCobertura(id);
                toast.success('Cobertura excluída com sucesso!');
                fetchCoberturas();
            } catch (err) {
                toast.error('Erro ao excluir cobertura.');
                console.error(err);
            }
        }
    };

    return (
        <Box sx={{ p: 3 }}>
            <MainLayout>
                <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
                    <Grid container spacing={3}>
                        <Grid size={12}>
                            <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                    <Typography variant="h6" component="h2">
                                        Gerenciar Coberturas
                                    </Typography>
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        startIcon={<AddIcon />}
                                        onClick={() => handleOpen()}
                                    >
                                        Nova Cobertura
                                    </Button>
                                </Box>

                                {loading && <CircularProgress sx={{ alignSelf: 'center' }} />}
                                {error && <Alert severity="error">{error}</Alert>}

                                {!loading && !error && (
                                    <TableContainer>
                                        <Table>
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell>Nome da Cobertura</TableCell>
                                                    <TableCell align="right">Ações</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {coberturas.map((cobertura) => (
                                                    <TableRow key={cobertura.id}>
                                                        <TableCell>{cobertura.nome}</TableCell>
                                                        <TableCell align="right">
                                                            <IconButton color="primary" onClick={() => handleOpen(cobertura)}>
                                                                <EditIcon />
                                                            </IconButton>
                                                            <IconButton color="error" onClick={() => handleDelete(cobertura.id)}>
                                                                <DeleteIcon />
                                                            </IconButton>
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                )}
                            </Paper>
                        </Grid>
                    </Grid>
                </Container>

                <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
                    <DialogTitle>{isEditing ? 'Editar Cobertura' : 'Nova Cobertura'}</DialogTitle>
                    <DialogContent>
                        <TextField
                            autoFocus
                            margin="dense"
                            label="Nome da Cobertura"
                            type="text"
                            fullWidth
                            variant="outlined"
                            value={currentCobertura.nome}
                            onChange={(e) => setCurrentCobertura({ ...currentCobertura, nome: e.target.value.toUpperCase() })}
                        />
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleClose}>Cancelar</Button>
                        <Button onClick={handleSave} variant="contained">Salvar</Button>
                    </DialogActions>
                </Dialog>
                <ToastContainer />
            </MainLayout>
        </Box>
    );
};

export default GerenciarCoberturas;